// src/components/billing/ViewBillingItem.jsx
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BillingItemsService from "../../../../services/BillingItemsService";
import { AuthContext } from "../../../../context/AuthContext";

const ViewBillingItem = () => {
  const { billingItemId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [billingItem, setBillingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [newPayment, setNewPayment] = useState({
    description: "",
    amount: "",
    paidDate: new Date().toISOString().split("T")[0]
  });

  useEffect(() => {
    const adminCheck = ["ADMIN", "CEO", "VAIBHAV", "EMP"].includes(user?.role);
    setIsAdmin(adminCheck);

    const fetchBillingItem = async () => {
      try {
        setLoading(true);
        const data = await BillingItemsService.getBillingItemById(billingItemId);
        setBillingItem(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching billing item:", err);
        setError("Failed to load billing item details.");
        setLoading(false);
      }
    };

    fetchBillingItem();
  }, [billingItemId, user]);

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setNewPayment({
      ...newPayment,
      [name]: value
    });
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await BillingItemsService.addPayment(billingItemId, newPayment);
      
      // Reset form
      setNewPayment({
        description: "",
        amount: "",
        paidDate: new Date().toISOString().split("T")[0]
      });
      setShowPaymentForm(false);
      
      // Refresh billing item
      const updatedBillingItem = await BillingItemsService.getBillingItemById(billingItemId);
      setBillingItem(updatedBillingItem);
      
      setLoading(false);
    } catch (err) {
      console.error("Error adding payment:", err);
      setError("Failed to add payment.");
      setLoading(false);
    }
  };

  const handleDeleteBillingItem = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this billing item? This action cannot be undone."
      )
    ) {
      try {
        setLoading(true);
        await BillingItemsService.deleteBillingItem(billingItemId);
        navigate("/dashboard/billing-items");
      } catch (err) {
        console.error("Error deleting billing item:", err);
        setError("Failed to delete billing item.");
        setLoading(false);
      }
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor = "bg-gray-100 text-gray-800";

    switch (status) {
      case "PENDING":
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      case "PAID":
        bgColor = "bg-green-100 text-green-800";
        break;
      case "OVERDUE":
        bgColor = "bg-red-100 text-red-800";
        break;
      case "CANCELLED":
        bgColor = "bg-gray-100 text-gray-800";
        break;
      case "DRAFT":
        bgColor = "bg-gray-100 text-gray-800";
        break;
      default:
        bgColor = "bg-gray-100 text-gray-800";
    }

    return (
      <span
        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor}`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  if (!billingItem) {
    return (
      <div className="bg-yellow-100 text-yellow-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Billing Item Not Found</p>
        <p>The billing item you're looking for doesn't exist or you don't have permission to view it.</p>
      </div>
    );
  }

  // Calculate total paid and balance
  const totalPaid = billingItem.paidAmount
    ? billingItem.paidAmount.reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
    : 0;
  const balance = parseFloat(billingItem.amount) - totalPaid;

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{billingItem.title}</h1>
          <div className="mt-2">
            <StatusBadge status={billingItem.status} />
          </div>
        </div>

        {isAdmin && (
          <div className="flex space-x-2">
            {billingItem.status !== 'PAID' && (
              <button
                onClick={() => navigate(`/dashboard/billing-items/${billingItemId}/edit`)}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
              >
                Edit
              </button>
            )}
            <button
              onClick={handleDeleteBillingItem}
              className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Billing Item Details */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Billing Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Description</p>
            <p className="text-gray-900 mt-1">{billingItem.description}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Amount</p>
            <p className="text-gray-900 mt-1">
              {billingItem.currency} {parseFloat(billingItem.amount).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Due Date</p>
            <p className="text-gray-900 mt-1">{formatDate(billingItem.dueDate)}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Status</p>
            <p className="text-gray-900 mt-1">
              <StatusBadge status={billingItem.status} />
            </p>
          </div>
        </div>
      </div>

      {/* Project and Client Info */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Related Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {billingItem.project && (
            <div>
              <p className="text-sm font-medium text-gray-500">Project</p>
              <p className="text-gray-900 mt-1">
                {typeof billingItem.project === 'object' ? billingItem.project.name : 'Loading...'}
              </p>
              {typeof billingItem.project === 'object' && (
                <button
                  onClick={() => navigate(`/dashboard/projects/${billingItem.project._id}`)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Project
                </button>
              )}
            </div>
          )}
          {billingItem.client && (
            <div>
              <p className="text-sm font-medium text-gray-500">Client</p>
              <p className="text-gray-900 mt-1">
                {typeof billingItem.client === 'object' ? billingItem.client.companyName : 'Loading...'}
              </p>
              {typeof billingItem.client === 'object' && billingItem.client.user && (
                <button
                  onClick={() => navigate(`/dashboard/client/${billingItem.client.user._id || billingItem.client.user}`)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Client
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Summary */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Payment Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white p-4 rounded-md shadow-sm">
            <p className="text-sm font-medium text-gray-500">Total Amount</p>
            <p className="text-2xl font-bold text-gray-800">
              {billingItem.currency} {parseFloat(billingItem.amount).toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm">
            <p className="text-sm font-medium text-gray-500">Amount Paid</p>
            <p className="text-2xl font-bold text-green-600">
              {billingItem.currency} {totalPaid.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm">
            <p className="text-sm font-medium text-gray-500">Balance Due</p>
            <p className="text-2xl font-bold text-red-600">
              {billingItem.currency} {balance.toLocaleString()}
            </p>
          </div>
        </div>

        {isAdmin && billingItem.status !== 'PAID' && (
          <div className="flex justify-end">
            <button
              onClick={() => setShowPaymentForm(!showPaymentForm)}
              className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded"
            >
              {showPaymentForm ? "Cancel" : "Add Payment"}
            </button>
          </div>
        )}

        {/* Add Payment Form */}
        {showPaymentForm && isAdmin && (
          <div className="mt-4 bg-white p-4 rounded-md border border-gray-200">
            <h3 className="text-md font-semibold text-gray-700 mb-3">Add Payment</h3>
            <form onSubmit={handleAddPayment}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={newPayment.description}
                    onChange={handlePaymentInputChange}
                    placeholder="e.g., Bank Transfer"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={newPayment.amount}
                    onChange={handlePaymentInputChange}
                    min="0"
                    step="0.01"
                    max={balance}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    name="paidDate"
                    value={newPayment.paidDate}
                    onChange={handlePaymentInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Payment History */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Payment History</h2>
        {billingItem.paidAmount && billingItem.paidAmount.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {billingItem.paidAmount.map((payment, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.description || "Payment"}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {billingItem.currency} {parseFloat(payment.amount).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(payment.paidDate)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500">No payments recorded yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewBillingItem;